/*
 * Copyright (c) 2008-present Sonatype, Inc.
 *
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/pro/attributions
 * Sonatype and Sonatype Nexus are trademarks of Sonatype, Inc. Apache Maven is a trademark of the Apache Foundation.
 * M2Eclipse is a trademark of the Eclipse Foundation. All other trademarks are the property of their respective owners.
 */
import React from 'react';
import { useMachine, useService } from '@xstate/react';
import { NxFontAwesomeIcon } from 'nexus-ui-plugin';
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { faAtlassian } from '@fortawesome/free-brands-svg-icons';
import CrowdSettingsMachine from './CrowdSettingsMachine';
import {
  Checkbox,
  ContentBody,
  FieldWrapper,
  NxButton,
  NxLoadWrapper,
  NxSubmitMask,
  NxTooltip,
  Page,
  PageHeader,
  PageTitle,
  Section,
  SectionFooter,
  Textfield,
  Utils
} from 'nexus-ui-plugin';
import UIStrings from '../../../../constants/UIStrings';

export default function CrowdSettings() {
  const service = useMachine(CrowdSettingsMachine, {devTools: true})[2];
  return <CrowdSettingsForm service={service}/>;
}

export function CrowdSettingsForm({service}) {
  const [current, send] = useService(service);

  const context = current.context;
  const isLoading = current.matches('loading');
  const isSaving = current.matches('saving');
  const settings = context.data;
  const isPristine = context.isPristine;
  const isInvalid = Utils.isInvalid(context.validationErrors);
  const hasData = settings && settings !== {};

  function updateSettings({target}) {
    const value = (function () {
      if (target.type === 'checkbox') {
        return target.checked;
      }
      else {
        return target.value ? target.value : '';
      }
    })();
    const name = target.name || target.id;
    send('UPDATE', {
      data: {
        [name]: value
      }
    });
  }

  function saveSettings(event) {
    event.preventDefault();
    send('SAVE');
  }

  function discardChanges() {
    send('RESET');
  }

  function verifyConnection() {
    send('VERIFY-CONNECTION');
  }

  function clearCache() {
    send('CLEAR-CACHE');
  }

  function handleEnter(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      send('SAVE');
    }
  }

  return <Page>
    <PageHeader><PageTitle icon={faAtlassian} {...UIStrings.CROWD_SETTINGS.MENU}/></PageHeader>
    <ContentBody className='nxrm-crowd-settings'>
      <Section>
        <NxLoadWrapper loading={isLoading}>
        {hasData && <>
          {isSaving && <NxSubmitMask message={UIStrings.SAVING}/>}
          <FieldWrapper
              labelText={UIStrings.CROWD_SETTINGS.FIELDS.enabledLabel}
          >
            <Checkbox
                checkboxId='enabled'
                isChecked={settings.enabled}
                onChange={updateSettings}
                onKeyPress={handleEnter}
            >
              {UIStrings.CROWD_SETTINGS.FIELDS.enabledDescription}
            </Checkbox>
          </FieldWrapper>
          <FieldWrapper
              labelText={UIStrings.CROWD_SETTINGS.FIELDS.realmActiveLabel}
          >
            <Checkbox
                checkboxId='realmActive'
                isChecked={settings.realmActive}
                onChange={updateSettings}
                onKeyPress={handleEnter}
            >
              <span dangerouslySetInnerHTML={UIStrings.CROWD_SETTINGS.FIELDS.realmActiveDescription} />
            </Checkbox>
          </FieldWrapper>
          <FieldWrapper
              labelText={UIStrings.CROWD_SETTINGS.FIELDS.urlLabel}
              descriptionText={UIStrings.CROWD_SETTINGS.FIELDS.urlDescription}
          >
            <Textfield
                {...Utils.fieldProps('url', current)}
                onChange={updateSettings}
                onKeyPress={handleEnter}
            />
          </FieldWrapper>
          {settings.url?.startsWith('https') ?
              <FieldWrapper
                  labelText={UIStrings.CROWD_SETTINGS.FIELDS.useTrustStoreLabel}
              >
                <Checkbox
                    checkboxId='useTrustStoreForUrl'
                    isChecked={settings.useTrustStoreForUrl}
                    onChange={updateSettings}
                    onKeyPress={handleEnter}
                >
                  {UIStrings.CROWD_SETTINGS.FIELDS.useTrustStoreDescription}
                  <br />
                  <a href="#admin/security/sslcertificates">Configure the NXRM truststore</a>
                </Checkbox>
              </FieldWrapper>
              : null}
          <FieldWrapper
              labelText={UIStrings.CROWD_SETTINGS.FIELDS.applicationNameLabel}
          >
            <Textfield
                {...Utils.fieldProps('applicationName', current)}
                autoComplete="off"
                onChange={updateSettings}
                onKeyPress={handleEnter}
            />
          </FieldWrapper>
          <FieldWrapper
              labelText={UIStrings.CROWD_SETTINGS.FIELDS.applicationPasswordLabel}
          >
            <Textfield
                {...Utils.fieldProps('applicationPassword', current)}
                type="password"
                autoComplete="new-password"
                onChange={updateSettings}
                onKeyPress={handleEnter}
            />
          </FieldWrapper>
          <FieldWrapper
              labelText={UIStrings.CROWD_SETTINGS.FIELDS.timeoutLabel}
              descriptionText={UIStrings.CROWD_SETTINGS.FIELDS.timeoutDescription}
              isOptional
          >
            <Textfield
                {...Utils.fieldProps('timeout', current)}
                type="number"
                min="0"
                onChange={updateSettings}
                onKeyPress={handleEnter}
            />
          </FieldWrapper>

          <SectionFooter>
            <NxTooltip title={Utils.saveTooltip({isPristine, isInvalid})}>
              <NxButton variant="primary" className={(isPristine || isInvalid) && 'disabled'} onClick={saveSettings} type="submit">
                {UIStrings.SETTINGS.SAVE_BUTTON_LABEL}
              </NxButton>
            </NxTooltip>
            <NxTooltip title={Utils.discardTooltip({isPristine})}>
              <NxButton className={(isPristine) && 'disabled'} onClick={discardChanges}>
                {UIStrings.SETTINGS.DISCARD_BUTTON_LABEL}
              </NxButton>
            </NxTooltip>
            <NxButton disabled={isInvalid} onClick={verifyConnection}>
              {UIStrings.CROWD_SETTINGS.BUTTONS.VERIFY_BUTTON_LABEL}
            </NxButton>
            <NxButton onClick={clearCache}>
              <NxFontAwesomeIcon icon={faTrash}/>
              <span>{UIStrings.CROWD_SETTINGS.BUTTONS.CLEAR_BUTTON_LABEL}</span>
            </NxButton>
          </SectionFooter>
        </>}
        </NxLoadWrapper>
      </Section>
    </ContentBody>
  </Page>;
}
